<script setup lang="ts">
import dayjs from 'dayjs';
import { onMounted, ref } from 'vue';
import { isNilOrEmpty } from '~/utils';
import { useAuthStore } from '~/stores/auth';
import enumSelect from '~/graphql/enumSelect.graphql';
import usuarioSelect from '~/graphql/usuarioSelect.graphql';
import type { GraphqlDatatableColumn } from '~/components/GqlDatatable.vue';
import { getCaqEnumValues } from '~/enums/tipoCompromissoEnum';

const props = defineProps({
  pastaCodigos: {
    default: () => null,
    type: Array,
  },
  publicacaoNumeroProcessoCodigos: {
    default: () => null,
    type: Array,
  },
  compromisso: {
    default: null,
    type: Object,
  },
  dataPrazo: {
    default: null,
    type: String,
  },
  descricao: {
    default: null,
    type: String,
  },
  tipo: {
    default: null,
    type: Number,
  },
});

const emit = defineEmits(['close', 'loaded', 'success', 'error']);

const mostrarHistoricoSolicitacoes = ref('h');
const colunas = [
  {
    key: 'compromisso',
    title: 'COMPROMISSO',
    width: 120,
  },
  {
    key: 'responsavel',
    title: 'RESPONSÁVEL',
    width: 120,
  },
  {
    dataIndex: 'dataInicioText',
    key: 'dataInicio',
    sortBy: ['dataInicio'],
    sorter: true,
    title: 'SOLICITADO EM',
    width: 120,
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'dataVencimentoText',
    key: 'dataVencimento',
    sortBy: ['dataVencimento'],
    sorter: true,
    title: 'VENCIMENTO EM',
    width: 120,
  },
  {
    dataIndex: 'dataConclusaoText',
    key: 'dataConclusao',
    sortBy: ['dataConclusao'],
    sorter: true,
    title: 'CONCLUÍDO EM',
    width: 120,
  },
] as GraphqlDatatableColumn[];
const authStore = useAuthStore();

function disabledDate(current: dayjs.Dayjs): boolean
{
  return (
    current.day() === 0
    || current.day() === 6
    || current <= dayjs().subtract(1, 'day').endOf('day')
  );
}

onMounted(async () =>
{
  emit('loaded');
});
</script>

<template>
  <CompromissoJobCommandForm
    command="CriarCompromissoCaqCommand"
    :show-upload-files="true"
    :submit-button-disabled="!isNilOrEmpty(compromisso)
      && compromisso.responsavel.codigo !== authStore.codigoUsuario"
    :initial-model="{
      pastaCodigos: props.pastaCodigos,
      publicacaoNumeroProcessoCodigos: props.publicacaoNumeroProcessoCodigos,
      compromissoCodigos: props.compromisso ? [props.compromisso.codigo] : null,
      tipo: props.tipo,
      dataPrazo: props.dataPrazo,
      descricao: props.descricao,
    }"
    @close="() => emit('close')"
  >
    <template #form="{ model }">
      <a-alert
        v-if="pastaCodigos?.length > 1"
        type="warning"
        message="Você irá realizar uma alteração em lote"
        show-icon
        style="margin-bottom: 24px"
      />

      <a-collapse
        v-if="props.pastaCodigos?.length === 1"
        v-model:activeKey="mostrarHistoricoSolicitacoes"
        style="margin-bottom: 24px"
      >
        <a-collapse-panel
          key="h"
          header="HISTÓRICO DE SOLICITAÇÕES"
        >
          <CompromissoGqlDatatable
            :columns="colunas"
            :show-dates-filter="false"
            :show-reload-button="false"
            :show-status-search-bar="false"
            :show-filter="false"
            :variables="{
              order: [{ dataInicio: 'DESC' }],
              where: {
                tipo: {
                  eq: 72,
                },
                fluxo: {
                  pastaCodigo: {
                    in: props.pastaCodigos,
                  },
                },
              },
            }"
          />
        </a-collapse-panel>
      </a-collapse>

      <a-form-item
        v-if="props.tipo === null"
        label="Tipo"
        name="tipo"
        :rules="{ required: true, message: 'Tipo é obrigatório' }"
      >
        <GqlSelect
          v-model:value="model.tipo"
          :query="enumSelect"
          :variables="{
            name: 'TipoCompromissoEnum',
            where: {
              value: {
                in: getCaqEnumValues(),
              },
            },
          }"
          placeholder="Selecione o tipo da pasta"
        />
      </a-form-item>

      <a-form-item
        v-if="props.descricao === null"
        label="Descrição"
        name="descricao"
        style="padding-bottom: 0"
        :rules="[{ message: 'Descrição é obrigatório', required: true }]"
      >
        <a-textarea
          v-model:value="model.descricao"
          :rows="4"
        />
      </a-form-item>

      <a-form-item
        label="Responsável"
        name="responsavel"
        :rules="{ required: true, message: 'Responsável é obrigatório' }"
      >
        <GqlSelect
          v-model:value="model.responsavel"
          :query="usuarioSelect"
          :variables="{
            where: {
              ativo: {
                eq: true,
              },
            },
            order: [
              {
                nome: 'ASC',
              },
            ],
          }"
          placeholder="Selecione o responsável"
          :allow-clear="true"
        />
      </a-form-item>

      <!-- 76 é o compromisso CaqPrazoFatal que tem um vencimento padrão -->
      <a-form-item
        v-if="props.dataPrazo === null && model.tipo?.value !== 76"
        label="Prazo"
        name="dataPrazo"
        :rules="[{ message: 'Prazo é obrigatório', required: true }]"
      >
        <a-date-picker
          v-model:value="model.dataPrazo"
          format="DD/MM/YYYY HH:mm"
          value-format="DD/MM/YYYY HH:mm"
          placeholder="Selecione uma data"
          style="width: 100%"
          :disabled-date="disabledDate"
          :show-time="{ defaultValue: dayjs('17:00:00', 'HH:mm:ss') }"
        />
      </a-form-item>

      <a-alert
        v-if="model.tipo?.value === 76"
        message="O vencimento para o prazo fatal é hoje às 23:59:59"
        type="info"
        show-icon
        style="margin-bottom: 16px"
      />
    </template>
  </CompromissoJobCommandForm>
</template>
